// src/components/attestations/AttestationForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createAttestation,
  updateAttestation,
  fetchAttestation,
  clearAttestationError,
  clearAttestationSuccess,
} from '../../store/actions/attestationActions';
import { fetchAgencies } from '../../store/actions/agencyActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const AttestationForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedAttestation, loading, error, success } = useSelector(
    (state) => state.attestations
  );
  const { agencies } = useSelector((state) => state.agencies);

  const [formData, setFormData] = useState({
    // Personal Information
    title: '',
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    
    // Identification Numbers
    numEmission: '',
    numAttestation: '',
    numPolice: '',
    idNumber: '',
    drivingLicense: '',
    insuranceNumber: '',
    
    // Dates
    DateEmission: '',
    DateEffet: '',
    DateEcheance: '',
    
    // Financial
    primeNet: '',
    PrimeTTC: '',
    accessories: '',
    
    // Relations
    senderId: '',
    receiverId: '',
    
    // Status
    status: 'PENDING',
  });

  const [errors, setErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState('personal');

  const isEditMode = !!id;

  const sections = [
    { id: 'personal', label: 'Informations personnelles', icon: '👤' },
    { id: 'identification', label: 'Numéros d\'identification', icon: '🔢' },
    { id: 'dates', label: 'Dates', icon: '📅' },
    { id: 'financial', label: 'Informations financières', icon: '💰' },
    { id: 'agencies', label: 'Agences', icon: '🏢' },
  ];

  const titleOptions = ['Mr', 'Mrs', 'Dr', 'Prof'];
  const genderOptions = ['Male', 'Female', 'Non-binary'];
  const statusOptions = [
    { value: 'PENDING', label: 'En attente' },
    { value: 'ACTIVE', label: 'Actif' },
    { value: 'CANCELLED', label: 'Annulé' },
    { value: 'EXPIRED', label: 'Expiré' },
    { value: 'ARCHIVED', label: 'Archivé' },
  ];

  // Styles object
  const styles = {
    container: {
      maxWidth: '1024px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    navigation: {
      display: 'flex',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      overflowX: 'auto',
    },
    navItem: {
      padding: '1rem 1.5rem',
      background: 'none',
      border: 'none',
      borderBottom: '2px solid transparent',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      whiteSpace: 'nowrap',
      transition: 'all 0.2s',
    },
    navItemActive: {
      color: '#2563eb',
      borderBottomColor: '#2563eb',
      backgroundColor: '#ffffff',
    },
    content: {
      padding: '2rem',
    },
    section: {
      display: activeSection === 'personal' ? 'block' : 'none',
    },
    sectionTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1.5rem',
    },
    fullWidth: {
      gridColumn: 'span 2',
    },
    fieldGroup: {
      marginBottom: '1rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, boxShadow 0.2s',
    },
    inputFocus: {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    select: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      backgroundColor: '#ffffff',
    },
    inputError: {
      borderColor: '#dc2626',
    },
    errorText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#dc2626',
    },
    helperText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '2rem',
      paddingTop: '2rem',
      borderTop: '1px solid #e5e7eb',
    },
    navButtons: {
      display: 'flex',
      gap: '1rem',
    },
    navButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
    },
    navButtonHover: {
      backgroundColor: '#f9fafb',
    },
    submitButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    submitButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
    required: {
      color: '#dc2626',
      marginLeft: '0.25rem',
    },
  };

  useEffect(() => {
    dispatch(fetchAgencies({ limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchAttestation(id));
    }
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    if (isEditMode && selectedAttestation) {
      setFormData({
        // Personal Information
        title: selectedAttestation.title || '',
        firstName: selectedAttestation.firstName || '',
        middleName: selectedAttestation.middleName || '',
        lastName: selectedAttestation.lastName || '',
        dateOfBirth: selectedAttestation.dateOfBirth ? selectedAttestation.dateOfBirth.split('T')[0] : '',
        gender: selectedAttestation.gender || '',
        
        // Identification Numbers
        numEmission: selectedAttestation.numEmission || '',
        numAttestation: selectedAttestation.numAttestation || '',
        numPolice: selectedAttestation.numPolice || '',
        idNumber: selectedAttestation.idNumber || '',
        drivingLicense: selectedAttestation.drivingLicense || '',
        insuranceNumber: selectedAttestation.insuranceNumber || '',
        
        // Dates
        DateEmission: selectedAttestation.DateEmission ? selectedAttestation.DateEmission.split('T')[0] : '',
        DateEffet: selectedAttestation.DateEffet ? selectedAttestation.DateEffet.split('T')[0] : '',
        DateEcheance: selectedAttestation.DateEcheance ? selectedAttestation.DateEcheance.split('T')[0] : '',
        
        // Financial
        primeNet: selectedAttestation.primeNet || '',
        PrimeTTC: selectedAttestation.PrimeTTC || '',
        accessories: selectedAttestation.accessories || '',
        
        // Relations
        senderId: selectedAttestation.senderId || '',
        receiverId: selectedAttestation.receiverId || '',
        
        // Status
        status: selectedAttestation.status || 'PENDING',
      });
    }
  }, [selectedAttestation, isEditMode]);

  // Cleanup
  useEffect(() => {
    return () => {
      dispatch(clearAttestationError());
      dispatch(clearAttestationSuccess());
    };
  }, [dispatch]);

  // Validation function
  const validateForm = useCallback(() => {
    const newErrors = {};

    // Required fields
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Le prénom est requis';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Le nom est requis';
    }

    // Date validations
    if (formData.DateEffet && formData.DateEcheance) {
      if (new Date(formData.DateEffet) > new Date(formData.DateEcheance)) {
        newErrors.DateEcheance = "La date d'échéance doit être postérieure à la date d'effet";
      }
    }

    // Numeric validations
    if (formData.primeNet && isNaN(parseFloat(formData.primeNet))) {
      newErrors.primeNet = 'Doit être un nombre valide';
    }
    if (formData.PrimeTTC && isNaN(parseFloat(formData.PrimeTTC))) {
      newErrors.PrimeTTC = 'Doit être un nombre valide';
    }
    if (formData.accessories && isNaN(parseFloat(formData.accessories))) {
      newErrors.accessories = 'Doit être un nombre valide';
    }

    return newErrors;
  }, [formData]);

  const handleChange = useCallback((e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : parseFloat(value)) : value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  }, [errors]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      // Switch to the section with errors
      if (validationErrors.firstName || validationErrors.lastName) {
        setActiveSection('personal');
      } else if (validationErrors.DateEcheance) {
        setActiveSection('dates');
      } else if (validationErrors.primeNet || validationErrors.PrimeTTC || validationErrors.accessories) {
        setActiveSection('financial');
      }
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        await dispatch(updateAttestation(id, formData));
      } else {
        await dispatch(createAttestation(formData));
      }
      navigate('/admin/attestations');
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, formData, id, isEditMode, navigate, validateForm]);

  const handleCancel = useCallback(() => {
    if (Object.values(formData).some(value => value !== '' && value !== null && value !== undefined)) {
      setShowCancelConfirm(true);
    } else {
      navigate('/admin/attestations');
    }
  }, [formData, navigate]);

  const goToNextSection = () => {
    const currentIndex = sections.findIndex(s => s.id === activeSection);
    if (currentIndex < sections.length - 1) {
      setActiveSection(sections[currentIndex + 1].id);
    }
  };

  const goToPreviousSection = () => {
    const currentIndex = sections.findIndex(s => s.id === activeSection);
    if (currentIndex > 0) {
      setActiveSection(sections[currentIndex - 1].id);
    }
  };

  if (loading && isEditMode) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          {isEditMode ? "Modifier l'attestation" : 'Créer une nouvelle attestation'}
        </h1>
        <button
          onClick={handleCancel}
          style={styles.cancelButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
          }}
        >
          Annuler
        </button>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearAttestationError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearAttestationSuccess())}
        />
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Section Navigation */}
        <div style={styles.navigation}>
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              style={{
                ...styles.navItem,
                ...(activeSection === section.id ? styles.navItemActive : {}),
              }}
              onClick={() => setActiveSection(section.id)}
            >
              <span>{section.icon}</span>
              {section.label}
            </button>
          ))}
        </div>

        {/* Form Content */}
        <div style={styles.content}>
          {/* Personal Information Section */}
          <div style={activeSection === 'personal' ? {} : { display: 'none' }}>
            <h2 style={styles.sectionTitle}>Informations personnelles</h2>
            <div style={styles.grid}>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="title">
                  Civilité
                </label>
                <select
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  style={styles.select}
                >
                  <option value="">Sélectionner</option>
                  {titleOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="gender">
                  Genre
                </label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  style={styles.select}
                >
                  <option value="">Sélectionner</option>
                  {genderOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="firstName">
                  Prénom <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.firstName ? styles.inputError : {}),
                  }}
                />
                {errors.firstName && (
                  <div style={styles.errorText}>{errors.firstName}</div>
                )}
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="middleName">
                  Deuxième prénom
                </label>
                <input
                  type="text"
                  id="middleName"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="lastName">
                  Nom <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.lastName ? styles.inputError : {}),
                  }}
                />
                {errors.lastName && (
                  <div style={styles.errorText}>{errors.lastName}</div>
                )}
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="dateOfBirth">
                  Date de naissance
                </label>
                <input
                  type="date"
                  id="dateOfBirth"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>
            </div>
          </div>

          {/* Identification Section */}
          <div style={activeSection === 'identification' ? {} : { display: 'none' }}>
            <h2 style={styles.sectionTitle}>Numéros d'identification</h2>
            <div style={styles.grid}>
              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="numAttestation">
                    Numéro d'attestation
                  </label>
                  <input
                    type="text"
                    id="numAttestation"
                    name="numAttestation"
                    value={formData.numAttestation}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Généré automatiquement si vide"
                  />
                  <div style={styles.helperText}>
                    Laissez vide pour génération automatique
                  </div>
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="numPolice">
                  Numéro de police
                </label>
                <input
                  type="text"
                  id="numPolice"
                  name="numPolice"
                  value={formData.numPolice}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="numEmission">
                  Numéro d'émission
                </label>
                <input
                  type="text"
                  id="numEmission"
                  name="numEmission"
                  value={formData.numEmission}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="idNumber">
                  Numéro d'identité national
                </label>
                <input
                  type="text"
                  id="idNumber"
                  name="idNumber"
                  value={formData.idNumber}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="drivingLicense">
                  Permis de conduire
                </label>
                <input
                  type="text"
                  id="drivingLicense"
                  name="drivingLicense"
                  value={formData.drivingLicense}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="insuranceNumber">
                  Numéro d'assurance
                </label>
                <input
                  type="text"
                  id="insuranceNumber"
                  name="insuranceNumber"
                  value={formData.insuranceNumber}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>
            </div>
          </div>

          {/* Dates Section */}
          <div style={activeSection === 'dates' ? {} : { display: 'none' }}>
            <h2 style={styles.sectionTitle}>Dates</h2>
            <div style={styles.grid}>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="DateEmission">
                  Date d'émission
                </label>
                <input
                  type="date"
                  id="DateEmission"
                  name="DateEmission"
                  value={formData.DateEmission}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="DateEffet">
                  Date d'effet
                </label>
                <input
                  type="date"
                  id="DateEffet"
                  name="DateEffet"
                  value={formData.DateEffet}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="DateEcheance">
                  Date d'échéance
                </label>
                <input
                  type="date"
                  id="DateEcheance"
                  name="DateEcheance"
                  value={formData.DateEcheance}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.DateEcheance ? styles.inputError : {}),
                  }}
                />
                {errors.DateEcheance && (
                  <div style={styles.errorText}>{errors.DateEcheance}</div>
                )}
              </div>
            </div>
          </div>

          {/* Financial Section */}
          <div style={activeSection === 'financial' ? {} : { display: 'none' }}>
            <h2 style={styles.sectionTitle}>Informations financières</h2>
            <div style={styles.grid}>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="primeNet">
                  Prime nette (FCFA)
                </label>
                <input
                  type="number"
                  id="primeNet"
                  name="primeNet"
                  value={formData.primeNet}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  style={{
                    ...styles.input,
                    ...(errors.primeNet ? styles.inputError : {}),
                  }}
                />
                {errors.primeNet && (
                  <div style={styles.errorText}>{errors.primeNet}</div>
                )}
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="PrimeTTC">
                  Prime TTC (FCFA)
                </label>
                <input
                  type="number"
                  id="PrimeTTC"
                  name="PrimeTTC"
                  value={formData.PrimeTTC}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  style={{
                    ...styles.input,
                    ...(errors.PrimeTTC ? styles.inputError : {}),
                  }}
                />
                {errors.PrimeTTC && (
                  <div style={styles.errorText}>{errors.PrimeTTC}</div>
                )}
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="accessories">
                  Accessoires (FCFA)
                </label>
                <input
                  type="number"
                  id="accessories"
                  name="accessories"
                  value={formData.accessories}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  style={{
                    ...styles.input,
                    ...(errors.accessories ? styles.inputError : {}),
                  }}
                />
                {errors.accessories && (
                  <div style={styles.errorText}>{errors.accessories}</div>
                )}
              </div>
            </div>
          </div>

          {/* Agencies Section */}
          <div style={activeSection === 'agencies' ? {} : { display: 'none' }}>
            <h2 style={styles.sectionTitle}>Agences</h2>
            <div style={styles.grid}>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="senderId">
                  Agence émettrice
                </label>
                <select
                  id="senderId"
                  name="senderId"
                  value={formData.senderId}
                  onChange={handleChange}
                  style={styles.select}
                >
                  <option value="">Sélectionner une agence</option>
                  {agencies.map(agency => (
                    <option key={agency.id} value={agency.id}>
                      {agency.name} - {agency.city || ''}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="receiverId">
                  Agence réceptrice
                </label>
                <select
                  id="receiverId"
                  name="receiverId"
                  value={formData.receiverId}
                  onChange={handleChange}
                  style={styles.select}
                >
                  <option value="">Sélectionner une agence</option>
                  {agencies.map(agency => (
                    <option key={agency.id} value={agency.id}>
                      {agency.name} - {agency.city || ''}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="status">
                  Statut
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  style={styles.select}
                >
                  {statusOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div style={styles.buttonGroup}>
            <div style={styles.navButtons}>
              <button
                type="button"
                onClick={goToPreviousSection}
                style={styles.navButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.navButtonHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = styles.navButton.backgroundColor;
                }}
              >
                ← Précédent
              </button>
              <button
                type="button"
                onClick={goToNextSection}
                style={styles.navButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.navButtonHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = styles.navButton.backgroundColor;
                }}
              >
                Suivant →
              </button>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                ...styles.submitButton,
                ...(isSubmitting ? styles.submitButtonDisabled : {}),
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
                }
              }}
            >
              {isSubmitting 
                ? (isEditMode ? 'Modification...' : 'Création...') 
                : (isEditMode ? 'Modifier' : 'Créer')}
            </button>
          </div>
        </div>
      </form>

      {/* Cancel Confirmation */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => navigate('/admin/attestations')}
        title="Annuler les modifications"
        message="Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?"
        confirmText="Quitter"
        cancelText="Continuer l'édition"
        type="warning"
      />
    </div>
  );
};

export default AttestationForm;